import React, { useState, useEffect } from "react";
import { TextField, AppBar, Toolbar, InputAdornment, Select, FormControl, InputLabel, Button, MenuItem, IconButton, Grid } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Container, Box, Typography, Modal, Paper } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Tooltip from "@mui/material/Tooltip";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

const Alert = React.forwardRef(function Alert(props, ref) {
	return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// ── User Guide content for Add User ───────────────────────────────────────
const addUserGuideSteps = [
  {
    section: "🏢 Branch Selection (Master Users Only)",
    steps: [
      { label: "Branch Dropdown", desc: "Visible only to Master users. Select the branch for which the new user account is being created. Non-master users are automatically assigned their own branch." },
    ],
  },
  {
    section: "👤 User Details",
    steps: [
      { label: "User Role", desc: "Select the role/group to assign to the new user (e.g. Admin, Teacher, Accountant). Roles like 'Parent' and 'Student' are excluded from this list. Changing the role resets the Department and Employee fields." },
      { label: "Department", desc: "Select the department the user belongs to. Changing the department resets the Employee field. This filters the available employees in the next dropdown." },
      { label: "Employee Name", desc: "Select the employee to link to this user account. This dropdown is disabled until a Department is selected. Employees are filtered by the selected department." },
      { label: "User Name", desc: "Enter the login username for the new user. This will be used to log into the system." },
    ],
  },
  {
    section: "🔐 Password",
    steps: [
      { label: "Password", desc: "Enter a password for the new user. Must be 8–12 characters long and contain at least one special character (!@#$%^&*). Use the eye icon to toggle visibility." },
      { label: "Confirm Password", desc: "Re-enter the password to confirm it matches. Both fields share the same visibility toggle." },
    ],
  },
  {
    section: "📅 Account Settings",
    steps: [
      { label: "Expiry Date", desc: "Set the date on which this user account will expire and become inactive. Required field." },
    ],
  },
  {
    section: "⬇️ Actions",
    steps: [
      { label: "SAVE", desc: "Validates all fields and submits the form to create the new user. A success or error message will appear in a popup. On success, the form is reset and you are navigated back." },
      { label: "RESET", desc: "Clears all form fields and validation errors without saving, allowing you to start fresh." },
    ],
  },
];

// ── Add User Guide Modal ───────────────────────────────────────────────────
const AddUserGuide = ({ open, onClose }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="sm"
    fullWidth
    PaperProps={{
      sx: {
        borderRadius: "14px",
        overflow: "hidden",
        boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
      },
    }}
  >
    {/* ── Gradient header — matches Payment/Admission guide style ── */}
    <DialogTitle
      sx={{
        background: "linear-gradient(135deg, #1a237e 0%, #1976d2 50%, #26c6da 100%)",
        color: "#fff",
        fontWeight: 700,
        fontSize: "1.15rem",
        textAlign: "center",
        py: 2.5,
        letterSpacing: "0.04em",
      }}
    >
      📘 Add User — User Guide
    </DialogTitle>

    {/* ── Scrollable content ── */}
    <DialogContent
      dividers
      sx={{
        px: 3,
        py: 2.5,
        maxHeight: "62vh",
        overflowY: "auto",
        backgroundColor: "#fafafa",
      }}
    >
      {addUserGuideSteps.map((group, gi) => (
        <Box key={gi} sx={{ mb: gi < addUserGuideSteps.length - 1 ? 3 : 0 }}>

          {/* Section heading */}
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.97rem",
              color: "#1565c0",
              mb: 1.5,
              pb: 0.5,
              borderBottom: "2px solid #bbdefb",
            }}
          >
            {group.section}
          </Typography>

          {/* Step list */}
          {group.steps.map((step, si) => (
            <Box
              key={si}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                mb: 1.2,
              }}
            >
              {/* Bullet dot */}
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "#1976d2",
                  mt: "5px",
                  flexShrink: 0,
                }}
              />

              {/* Step text */}
              <Typography sx={{ fontSize: "0.875rem", color: "#333", lineHeight: 1.6 }}>
                {/* Bold label */}
                <Box component="span" sx={{ fontWeight: 700, color: "#111" }}>
                  {step.label}
                </Box>
                {" — "}
                {step.desc}
              </Typography>
            </Box>
          ))}
        </Box>
      ))}

      {/* Bottom tip */}
      <Box
        sx={{
          mt: 2.5,
          p: 1.5,
          borderRadius: "8px",
          backgroundColor: "#e3f2fd",
          border: "1px solid #90caf9",
        }}
      >
        <Typography sx={{ fontSize: "0.8rem", color: "#1565c0", fontStyle: "italic" }}>
          💡 <strong>Tip:</strong> Always select <strong>User Role</strong> and <strong>Department</strong> first before choosing an Employee. The password must be <strong>8–12 characters</strong> and include at least one special character.
        </Typography>
      </Box>
    </DialogContent>

    {/* ── Close button ── */}
    <DialogActions sx={{ justifyContent: "center", py: 2, backgroundColor: "#fafafa" }}>
      <Button
        onClick={onClose}
        variant="contained"
        sx={{
          background: "linear-gradient(45deg, #1976d2 30%, #26c6da 90%)",
          borderRadius: "25px",
          px: 5,
          py: 1,
          fontWeight: 600,
          textTransform: "none",
          fontSize: "0.9rem",
          boxShadow: "0 3px 8px rgba(25,118,210,0.35)",
          "&:hover": {
            background: "linear-gradient(45deg, #1565c0 30%, #00acc1 90%)",
          },
        }}
      >
        Close
      </Button>
    </DialogActions>
  </Dialog>
);

const AddUser = ({ onSave, onCancel }) => {
	const [departments, setDepartments] = useState([]);

	const navigate = useNavigate();
const [isMaster, setIsMaster] = useState(false);
const [branches, setBranches] = useState([]);
const [selectedBranchId, setSelectedBranchId] = useState(1);

	const [openSnackbar, setOpenSnackbar] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState("");
	const [snackbarSeverity, setSnackbarSeverity] = useState("success");

	const [modalOpen, setModalOpen] = useState(false);
	const [modalMessage, setModalMessage] = useState("");
	const [modalType, setModalType] = useState("success");
	const [userGroupId, setUserGroupId] = useState(0);
	const [employeeId, setEmployeeId] = useState("");
	const [userName, setUserName] = useState("");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [expiryDate, setExpiryDate] = useState("");
	const [usertype, setUsertype] = useState("");
	const [department, setDepartment] = useState("");

	const [users, setUsers] = useState([]); // user groups / roles
	const [employees, setEmployees] = useState([]); // full employee list from API
	const [showPassword, setShowPassword] = useState(false);

	// ── User Guide modal state ──
	const [guideOpen, setGuideOpen] = useState(false);

	// Validation states
	const [errors, setErrors] = useState({
		usertype: "",
		department: "",
		employeeId: "",
		userName: "",
		password: "",
		confirmPassword: "",
		expiryDate: "",
	});

	const validateForm = () => {
		const newErrors = {
			usertype: usertype ? "" : "User role is required",
			department: department ? "" : "Department is required",
			employeeId: employeeId ? "" : "Employee name is required",
			userName: userName ? "" : "User name is required",
			password: "",
			confirmPassword: "",
			expiryDate: expiryDate ? "" : "Expiry date is required",
		};

		const passwordPattern = /^(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/;
		if (!password) {
			newErrors.password = "Password is required";
		} else if (password.length > 12) {
			newErrors.password = "Password cannot be more than 12 characters";
		} else if (!passwordPattern.test(password)) {
			newErrors.password =
				"Password must be at least 8 characters long and contain at least one special character.";
		}

		if (!confirmPassword) {
			newErrors.confirmPassword = "Confirm password is required";
		} else if (password !== confirmPassword) {
			newErrors.confirmPassword = "Passwords do not match";
		}

		setErrors(newErrors);
		return Object.values(newErrors).every((e) => e === "");
	};

	const handleSave = async () => {
		if (!validateForm()) return;

		const formattedDate = expiryDate.split("-").reverse().join("/");

		try {
			const apiUrl = process.env.REACT_APP_BASE_URL;
			const token = sessionStorage.getItem("token");
			const branchId = isMaster ? selectedBranchId : sessionStorage.getItem("branchId");
			const response = await fetch(`${apiUrl}/CPanel/AddUser?Branch_Id=${branchId}`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: token,
				},
				body: JSON.stringify({
					userGroupId,
					employeeId: String(employeeId),
					userName,
					password,
					expiryDate: formattedDate,
					usertype,
				}),
			});

			const data = await response.json();
			const message = (data && (data.msg || data.message)) || "User has been added successfully.";
			const status = (data && data.status && data.status.toString().toLowerCase()) || "";
			const lowerMsg = (message || "").toString().toLowerCase();
			const errorKeywords = ['already exist', 'already exists', 'failed', 'error', 'unable', 'cannot'];
			const messageHasError = status.includes('failed') || errorKeywords.some(k => lowerMsg.includes(k));

			setModalType(messageHasError ? 'error' : 'success');
			setModalMessage(message);
			setModalOpen(true);

			if (!messageHasError) {
				// Reset form only on success
				setUserGroupId(0);
				setDepartment("");
				setEmployeeId("");
				setUserName("");
				setPassword("");
				setConfirmPassword("");
				setExpiryDate("");
				setUsertype("");
				setErrors({});
			}

			if (response.ok && !messageHasError) {
				onSave?.(data);
			}
		} catch (error) {
			console.error("API request error:", error);
			setSnackbarMessage("An error occurred while saving");
			setSnackbarSeverity("error");
			setOpenSnackbar(true);
		}
	};

	const handleCancel = () => {
		setUserGroupId(0);
		setDepartment("");
		setEmployeeId("");
		setUserName("");
		setPassword("");
		setConfirmPassword("");
		setExpiryDate("");
		setUsertype("");
		setErrors({});
	};


	const fetchDepartments = async () => {
  try {
    const apiUrl = process.env.REACT_APP_BASE_URL;
    const token = sessionStorage.getItem("token");
    const response = await fetch(
      `${apiUrl}/Department/DepartmentName`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({}),
      }
    );
    if (response.ok) {
      const data = await response.json();
      // Extract department names only
      const departmentNames = Array.isArray(data)
        ? data.map((dept) => dept.departmentName).filter(Boolean)
        : [];
      setDepartments(departmentNames);
    }
  } catch (error) {
    console.error("Error fetching departments:", error);
  }
};

	const fetchBranches = async () => {
  try {
    const apiUrl = process.env.REACT_APP_BASE_URL;
    const token = sessionStorage.getItem("token");
    const response = await fetch(`${apiUrl}/Branch/GetBranch`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({}),
    });

    if (response.ok) {
      const data = await response.json();
      setBranches(data);
    } else {
      console.error("Failed to fetch branches");
    }
  } catch (error) {
    console.error("Error fetching branches:", error);
  }
};

	const fetchEmpData = async () => {
		try {
			const apiUrl = process.env.REACT_APP_BASE_URL;
			const token = sessionStorage.getItem("token");
			const branchId = isMaster ? selectedBranchId : sessionStorage.getItem("branchId");
			const response = await fetch(`${apiUrl}/Employee/GetEmployee?Branch_Id=${branchId}`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: token,
				},
				body: JSON.stringify({}),
			});

			const responseData = await response.json();

			if (
				responseData.data === null &&
				responseData.msg === "Record Not Found"
			) {
				setSnackbarMessage("No employees found");
				setSnackbarSeverity("warning");
				setOpenSnackbar(true);
				return;
			}

			setEmployees(responseData || []);
		} catch (error) {
			console.error("Error fetching employees:", error);
			setSnackbarMessage("Failed to load employees");
			setSnackbarSeverity("error");
			setOpenSnackbar(true);
		}
	};

	const fetchUserGroups = async () => {
		try {
			const apiUrl = process.env.REACT_APP_BASE_URL;
			const token = sessionStorage.getItem("token");
			const response = await fetch(`${apiUrl}/CPanel/GetUserGroup`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: token,
				},
				body: JSON.stringify({
					userGroupId: 0,
					operation: "Other",
					userType: sessionStorage.getItem("userType"),
				}),
			});

			const data = await response.json();
			if (data.data === null && data.msg === "Record Not Found") {
				console.warn("No user groups found");
				return;
			}

			setUsers(data || []);
		} catch (error) {
			console.error("Error fetching user groups:", error);
		}
	};

	useEffect(() => {
		fetchUserGroups();
		fetchEmpData();
		 fetchDepartments();
	}, []);

	useEffect(() => {
  const isMasterUser = sessionStorage.getItem("is_Master") === "true";
  setIsMaster(isMasterUser);

  if (isMasterUser) {
    fetchBranches();
  } else {
    const branchId = sessionStorage.getItem("branchId");
    setSelectedBranchId(branchId);
  }
}, []);




useEffect(() => {
  if (selectedBranchId) {
    fetchEmpData();
  }
}, [selectedBranchId]);

	

	// Filter employees based on selected department & role (if needed)
	const filteredEmployees = employees.filter((emp) => {
		const matchesDepartment = department
			? emp.departmentName === department
			: true;

		// You can add role-based filtering here if you have mapping between roles & departments
		// For now we filter mostly by department
		return matchesDepartment;
	});

	const togglePasswordVisibility = () => {
		setShowPassword(!showPassword);
	};

	return (
		<Container>
			<AppBar
  position="static"
  sx={{
    backgroundColor: "#0B1F3D",
    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
    mb:1
  }}
>
  <Toolbar sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 3 }}>

    {/* Left: Title + ℹ️ Button grouped together */}
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          letterSpacing: "0.5px",
          color: "#ffffff",
        }}
      >
        Add User
      </Typography>

      {/* ── ℹ️ User Guide Button — sits right next to "Add User" title ── */}
      <Tooltip title="View User Guide" placement="bottom" arrow>
        <IconButton
          onClick={() => setGuideOpen(true)}
          size="small"
          sx={{
            width: 34,
            height: 34,
            borderRadius: "50%",
            backgroundColor: "rgba(255,255,255,0.15)",
            border: "2px solid rgba(255,255,255,0.7)",
            color: "#fff",
            fontWeight: 900,
            fontSize: "1rem",
            fontFamily: "serif",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.3)",
              border: "2px solid #fff",
              transform: "scale(1.12)",
              boxShadow: "0 0 12px rgba(255,255,255,0.4)",
            },
          }}
        >
          i
        </IconButton>
      </Tooltip>
    </Box>

    {/* Right: Branch Dropdown — Master Users Only */}
   {isMaster && (
  <Box sx={{ position: "relative" }}>
    <FormControl size="small" sx={{ width: 220 }}>
      <InputLabel
        sx={{
          color: "rgba(255,255,255,0.6)",
          fontSize: "0.75rem",
          fontWeight: 500,
          letterSpacing: "0.6px",
          textTransform: "uppercase",
          top: "-2px",
          "&.Mui-focused": { color: "#64B5F6" },
          "&.MuiFormLabel-filled": { color: "rgba(255,255,255,0.7)" },
        }}
      >
        Branch
      </InputLabel>
      <Select
        value={selectedBranchId || ""}
        onChange={(e) => setSelectedBranchId(e.target.value)}
        label="Branch"
        startAdornment={
          <InputAdornment position="start">
            <LocationOnIcon sx={{ fontSize: 16, color: "rgba(255,255,255,0.5)", ml: 0.5 }} />
          </InputAdornment>
        }
        sx={{
          color: "#ffffff",
          fontWeight: 500,
          fontSize: "0.875rem",
          letterSpacing: "0.3px",
          backgroundColor: "rgba(255,255,255,0.07)",
          backdropFilter: "blur(8px)",
          borderRadius: "8px",
          transition: "background-color 0.2s ease, box-shadow 0.2s ease",

          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(255,255,255,0.15)",
            borderWidth: "1px",
            transition: "border-color 0.2s ease",
          },
          "&:hover": {
            backgroundColor: "rgba(255,255,255,0.11)",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(255,255,255,0.35)",
            },
          },
          "&.Mui-focused": {
            backgroundColor: "rgba(255,255,255,0.1)",
            boxShadow: "0 0 0 3px rgba(100,181,246,0.18)",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#64B5F6",
              borderWidth: "1.5px",
            },
          },
          "& .MuiSelect-icon": {
            color: "rgba(255,255,255,0.5)",
            transition: "color 0.2s ease",
          },
          "&.Mui-focused .MuiSelect-icon": {
            color: "#64B5F6",
          },
          "& .MuiSelect-select": {
            py: 1.1,
          },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              mt: 0.5,
              borderRadius: "10px",
              border: "1px solid rgba(11,31,61,0.12)",
              boxShadow: "0 8px 32px rgba(11,31,61,0.18), 0 2px 8px rgba(0,0,0,0.08)",
              overflow: "hidden",
              "& .MuiList-root": { py: 0.5 },
            },
          },
        }}
      >
        {branches.map((branch) => (
          <MenuItem
            key={branch.branchId}
            value={branch.branchId}
            sx={{
              fontSize: "0.875rem",
              fontWeight: 500,
              color: "#0B1F3D",
              px: 2,
              py: 1.1,
              mx: 0.5,
              borderRadius: "6px",
              transition: "background-color 0.15s ease",
              "&:hover": {
                backgroundColor: "rgba(11,31,61,0.07)",
              },
              "&.Mui-selected": {
                backgroundColor: "rgba(25,118,210,0.1)",
                color: "#1565C0",
                fontWeight: 600,
                "&:hover": {
                  backgroundColor: "rgba(25,118,210,0.15)",
                },
              },
            }}  
          >
            {branch.branchName}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </Box>
)}

  </Toolbar>
</AppBar>

			{/* ── User Guide Modal ── */}
			<AddUserGuide open={guideOpen} onClose={() => setGuideOpen(false)} />

			<Paper elevation={3} sx={{ padding: 4, mt: 1 }}>
				<Box component="form" noValidate autoComplete="off">
					<Grid container spacing={2}>
						{/* User Role */}
						<Grid item xs={12} sm={6}>
							<TextField
								label="User Role*"
								select
								fullWidth
								value={usertype}
								onChange={(e) => {
									const selectedRole = users.find(
										(role) => role.userGroupName === e.target.value
									);
									setUsertype(e.target.value);
									setUserGroupId(selectedRole ? selectedRole.userGroupId : 0);
									setErrors({ ...errors, usertype: "" });
									setDepartment("");
									setEmployeeId("");
								}}
								error={!!errors.usertype}
								helperText={errors.usertype}
							>
								{users
									.filter((role) => {
										const name = (role.userGroupName || "")
											.trim()
											.toLowerCase();
										return name !== "parent" && name !== "student";
									})
									.map((role) => (
										<MenuItem key={role.userGroupId} value={role.userGroupName}>
											{role.userGroupName}
										</MenuItem>
									))}
							</TextField>
						</Grid>

						{/* Department */}
						<Grid item xs={12} sm={6}>
							<TextField
								label="Department*"
								select
								fullWidth
								value={department}
								onChange={(e) => {
									setDepartment(e.target.value);
									setErrors({ ...errors, department: "" });
									setEmployeeId(""); // reset employee when department changes
								}}
								error={!!errors.department}
								helperText={errors.department}
							>
								<MenuItem value="">-- Select Department --</MenuItem>
								{departments.map((dep) => (
									<MenuItem key={dep} value={dep}>
										{dep}
									</MenuItem>
								))}
							</TextField>
						</Grid>

						{/* Employee Name */}
						<Grid item xs={12} sm={6}>
							<TextField
								label="Employee Name*"
								select
								fullWidth
								value={employeeId}
								onChange={(e) => {
									setEmployeeId(e.target.value);
									setErrors({ ...errors, employeeId: "" });
								}}
								error={!!errors.employeeId}
								helperText={errors.employeeId}
								disabled={!department}
							>
								<MenuItem value="">
									{department
										? "-- Select Employee --"
										: "Select Department First"}
								</MenuItem>
								{filteredEmployees.map((employee) => (
									<MenuItem
										key={employee.employeeID}
										value={employee.employeeID}
									>
										{employee.employeeName} ({employee.employeeCode || "-"})
									</MenuItem>
								))}
							</TextField>
						</Grid>

						{/* Username */}
						<Grid item xs={12} sm={6}>
							<TextField
								label="User Name*"
								fullWidth
								value={userName}
								onChange={(e) => {
									setUserName(e.target.value);
									setErrors({ ...errors, userName: "" });
								}}
								error={!!errors.userName}
								helperText={errors.userName}
							/>
						</Grid>

						{/* Password */}
						<Grid item xs={12} sm={6}>
							<TextField
								label="Password*"
								type={showPassword ? "text" : "password"}
								fullWidth
								value={password}
								onChange={(e) => {
									const newPassword = e.target.value;
									setPassword(newPassword);
									// ... (validation logic remains same)
									const newErrors = { ...errors };
									const passwordPattern =
										/^(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/;

									if (!newPassword) newErrors.password = "Password is required";
									else if (newPassword.length > 12)
										newErrors.password =
											"Password cannot be more than 12 characters";
									else if (!passwordPattern.test(newPassword))
										newErrors.password =
											"Password must be at least 8 characters long and contain at least one special character.";
									else newErrors.password = "";

									if (confirmPassword && newPassword !== confirmPassword) {
										newErrors.confirmPassword = "Passwords do not match";
									}

									setErrors(newErrors);
								}}
								error={!!errors.password}
								helperText={errors.password}
								InputProps={{
									endAdornment: (
										<IconButton onClick={togglePasswordVisibility}>
											{showPassword ? <VisibilityOff /> : <Visibility />}
										</IconButton>
									),
								}}
							/>
						</Grid>

						{/* Confirm Password */}
						<Grid item xs={12} sm={6}>
							<TextField
								label="Confirm Password*"
								type={showPassword ? "text" : "password"}
								fullWidth
								value={confirmPassword}
								onChange={(e) => {
									const newConfirm = e.target.value;
									setConfirmPassword(newConfirm);
									const newErrors = { ...errors };

									if (!newConfirm)
										newErrors.confirmPassword = "Confirm password is required";
									else if (password !== newConfirm)
										newErrors.confirmPassword = "Passwords do not match";
									else newErrors.confirmPassword = "";

									setErrors(newErrors);
								}}
								error={!!errors.confirmPassword}
								helperText={errors.confirmPassword}
								InputProps={{
									endAdornment: (
										<IconButton onClick={togglePasswordVisibility}>
											{showPassword ? <VisibilityOff /> : <Visibility />}
										</IconButton>
									),
								}}
							/>
						</Grid>

						{/* Expiry Date */}
						<Grid item xs={12} sm={6}>
							<TextField
								label="Expiry Date*"
								type="date"
								fullWidth
								value={expiryDate}
								onChange={(e) => {
									setExpiryDate(e.target.value);
									setErrors({ ...errors, expiryDate: "" });
								}}
								error={!!errors.expiryDate}
								helperText={errors.expiryDate}
								InputLabelProps={{ shrink: true }}
							/>
						</Grid>

						{/* Buttons */}
						<Grid item xs={12}>
							<Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
								<Button
									variant="contained"
									color="primary"
									onClick={handleSave}
								>
									SAVE
								</Button>
								<Button
									variant="contained"
									color="error"
									onClick={handleCancel}
								>
									RESET
								</Button>
							</Box>
						</Grid>
					</Grid>
				</Box>
			</Paper>

{/* Modal */}
		<Modal
			open={modalOpen}
			onClose={() => setModalOpen(false)}
			aria-labelledby="modal-title"
		>
			<Box
				sx={{
					position: "absolute",
					top: "50%",
					left: "50%",
					transform: "translate(-50%, -50%)",
					width: 400,
					bgcolor: "background.paper",
					borderRadius: 2,
					boxShadow: 24,
					overflow: "hidden",
				}}
			>
				<Box sx={{ backgroundColor: modalType === 'error' ? '#8B0000' : '#0B1F40', padding: "12px 16px" }}>
					<Typography
						id="modal-title"
						variant="h6"
						sx={{ color: "#fff" }}
					>
						{modalType === 'error' ? 'Error ❌' : 'Success ✅'}
					</Typography>
				</Box>
				<Box sx={{ padding: "20px" }}>
					<Typography sx={{ mb: 3 }}>
						{modalMessage || 'Operation completed.'}
					</Typography>
					<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
						<Button
							onClick={() => {
								setModalOpen(false);
								if (modalType !== 'error') navigate(-1);
								}}
								variant="contained"
								color="primary"
							>
								OK
							</Button>
						</Box>
					</Box>
				</Box>
			</Modal>

			<Snackbar
				open={openSnackbar}
				autoHideDuration={4000}
				onClose={() => setOpenSnackbar(false)}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<Alert
					onClose={() => setOpenSnackbar(false)}
					severity={snackbarSeverity}
					sx={{ width: "100%" }}
				>
					{snackbarMessage}
				</Alert>
			</Snackbar>
		</Container>
	);
};

export default AddUser;